import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// Gráfico que arma el asistente en un bloque ```chart (el formato lo
// define DOMIOT_CHAT_SYSTEM_PROMPT en el backend):
// {"type":"line","title":"…","x":"hora","series":[{"key":"temp","name":"Temperatura","unit":"°C"}],"data":[{"hora":"08:00","temp":11.2}]}
interface ChartSeries {
  key: string;
  name: string;
  unit?: string;
}

export interface ChartSpec {
  type: 'line' | 'bar' | 'area';
  title?: string;
  x: string;
  series: ChartSeries[];
  data: Array<Record<string, string | number | null>>;
}

const MAX_SERIES = 4;

export function parseChartSpec(source: string): ChartSpec | null {
  try {
    const raw = JSON.parse(source);
    if (!raw || typeof raw !== 'object') return null;
    const type = ['line', 'bar', 'area'].includes(raw.type) ? raw.type : 'line';
    if (typeof raw.x !== 'string' || !Array.isArray(raw.data) || raw.data.length === 0) return null;
    const series = (Array.isArray(raw.series) ? raw.series : [])
      .filter((s: unknown): s is ChartSeries => !!s && typeof (s as ChartSeries).key === 'string')
      .slice(0, MAX_SERIES)
      .map((s: ChartSeries) => ({ key: s.key, name: typeof s.name === 'string' ? s.name : s.key, unit: typeof s.unit === 'string' ? s.unit : undefined }));
    if (series.length === 0) return null;
    const data = raw.data.filter((row: unknown) => !!row && typeof row === 'object');
    return { type, title: typeof raw.title === 'string' ? raw.title : undefined, x: raw.x, series, data };
  } catch {
    return null;
  }
}

// Mismo color que usa el resto de la app para cada magnitud (temperatura en
// naranja, lluvia en azul...), elegido por la unidad; si no se reconoce, la
// paleta general en orden.
const COLOR_BY_UNIT: Record<string, string> = {
  '°c': 'var(--domiot-metric-temp)',
  '%': 'var(--domiot-metric-humidity)',
  mm: 'var(--domiot-metric-rain)',
  'km/h': 'var(--domiot-metric-wind)',
  hpa: 'var(--domiot-metric-pressure)',
};
const PALETTE = ['var(--domiot-accent)', 'var(--domiot-metric-rain)', 'var(--domiot-warning)', 'var(--domiot-metric-pressure)'];

function seriesColors(series: ChartSeries[]): string[] {
  const used = new Set<string>();
  return series.map((s, i) => {
    const byUnit = s.unit ? COLOR_BY_UNIT[s.unit.trim().toLowerCase()] : undefined;
    const color = byUnit && !used.has(byUnit) ? byUnit : PALETTE.find((c) => !used.has(c)) ?? PALETTE[i % PALETTE.length];
    used.add(color);
    return color;
  });
}

const tick = { fontSize: 11, fill: 'var(--domiot-text-muted)' };
const tooltipStyle = {
  background: 'var(--domiot-surface)',
  border: '1px solid var(--domiot-border)',
  borderRadius: 8,
  fontSize: 12,
};

const AiChart: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const colors = seriesColors(spec.series);
  // Con dos unidades distintas (°C y %), la segunda va en un eje a la derecha.
  const units = Array.from(new Set(spec.series.map((s) => s.unit ?? '')));
  const dualAxis = units.length === 2;
  const axisFor = (s: ChartSeries) => (dualAxis && (s.unit ?? '') === units[1] ? 'right' : 'left');
  const unitOf = new Map(spec.series.map((s) => [s.name, s.unit]));

  const Chart = spec.type === 'bar' ? BarChart : spec.type === 'area' ? AreaChart : LineChart;
  const marks = spec.series.map((s, i) => {
    const common = { dataKey: s.key, name: s.name, yAxisId: axisFor(s), isAnimationActive: false };
    if (spec.type === 'bar') return <Bar key={s.key} {...common} fill={colors[i]} radius={[3, 3, 0, 0]} maxBarSize={22} />;
    if (spec.type === 'area')
      return <Area key={s.key} {...common} type="monotone" stroke={colors[i]} strokeWidth={2} fill={colors[i]} fillOpacity={0.15} connectNulls />;
    return <Line key={s.key} {...common} type="monotone" stroke={colors[i]} strokeWidth={2.25} dot={spec.data.length <= 12} connectNulls />;
  });

  return (
    <figure className="ai-chart">
      {spec.title && <figcaption className="ai-chart-title">{spec.title}</figcaption>}
      {/* La unidad va arriba de cada eje y no en cada número: "km/h" o "hPa"
          al lado del valor no entran en el ancho del eje y se cortaban. */}
      {units.some(Boolean) && (
        <div className="ai-chart-units">
          <span>{units[0]}</span>
          {dualAxis && <span>{units[1]}</span>}
        </div>
      )}
      <ResponsiveContainer width="100%" height={220}>
        <Chart data={spec.data} margin={{ top: 8, right: dualAxis ? 0 : 8, bottom: 0, left: -12 }}>
          <CartesianGrid vertical={false} stroke="var(--domiot-border)" strokeDasharray="3 3" />
          <XAxis dataKey={spec.x} tick={tick} axisLine={{ stroke: 'var(--domiot-border)' }} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
          <YAxis yAxisId="left" tick={tick} axisLine={false} tickLine={false} width={40} />
          {dualAxis && (
            <YAxis yAxisId="right" orientation="right" tick={tick} axisLine={false} tickLine={false} width={40} />
          )}
          <Tooltip
            contentStyle={tooltipStyle}
            cursor={spec.type === 'bar' ? { fill: 'var(--domiot-surface-alt)' } : { stroke: 'var(--domiot-border)' }}
            formatter={(value, name) => [`${value}${unitOf.get(String(name)) ? ` ${unitOf.get(String(name))}` : ''}`, name]}
          />
          {spec.series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />}
          {marks}
        </Chart>
      </ResponsiveContainer>
    </figure>
  );
};

export default AiChart;
